import React, { useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaInstagram } from 'react-icons/fa';
import {
  FiBarChart2,
  FiChevronDown,
  FiCreditCard,
  FiEdit,
  FiLoader,
  FiLogOut,
  FiMenu,
  FiPlus,
  FiPrinter,
  FiSearch,
  FiSettings,
  FiX,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ACTIVE_REGISTRATION_YEAR,
  getInscricaoLifecycle,
} from '../../utils/subscriptionCycle';
import { isPagamentoPago } from '../../utils/paymentStatus';
import { EVENT } from '../../config/eventConfig';
import { useRegistrationStatus } from '../../hooks/useRegistrationStatus';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';
import AppHeader, {
  APP_HEADER_HEIGHT,
  APP_HEADER_HEIGHT_MOBILE,
} from '../shared/AppHeader';
import {
  MercadoPagoReturnHintModal,
  markMercadoPagoCheckoutPending,
  tryConsumeMercadoPagoCheckoutPending,
} from '../shared/MercadoPagoCheckoutModal.jsx';
import { PagamentoResumoModal } from '../shared/PagamentoResumoModal.jsx';
import { toast } from 'react-toastify';
import { isHttpOrHttpsUrl, PAYMENT_LINK_ERROR } from '../../utils/paymentUrl';

const PAGE_MAX_WIDTH = '760px';

const cardEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Container = styled.div`
  min-height: 100vh;
  background:
    radial-gradient(circle at top, rgba(255, 255, 255, 0.88) 0%, rgba(242, 242, 247, 0) 36%),
    linear-gradient(180deg, #f8f8fb 0%, #f2f2f7 42%, #eef1f6 100%);
  padding: calc(28px + ${APP_HEADER_HEIGHT}) 18px 40px;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Inter',
    'Segoe UI', sans-serif;
  color: var(--text-primary);
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;

  @media (max-width: 768px) {
    padding: calc(16px + ${APP_HEADER_HEIGHT_MOBILE}) 12px 28px;
  }
`;

const Content = styled.div`
  width: 100%;
  max-width: ${PAGE_MAX_WIDTH};
  margin: 0 auto;
`;

const HeaderIdentity = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

const HeaderBrand = styled.span`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8e8e93;
`;

const HeaderPageTitleRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  min-width: 0;
`;

const HeaderPageTitle = styled.h1`
  margin: 0;
  font-size: 18px;
  line-height: 1.2;
  letter-spacing: -0.02em;
  font-weight: 700;
  color: var(--text-primary);
`;

const HeaderPageMeta = styled.span`
  font-size: 12px;
  color: var(--text-secondary);
  font-weight: 500;
`;

const HeaderMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const UserBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 28px;
  width: fit-content;
  padding: 0 10px 0 8px;
  border-radius: 999px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: rgba(255, 255, 255, 0.74);
  color: #475569;
  backdrop-filter: blur(10px);
`;

const UserAvatar = styled.span`
  width: 20px;
  height: 20px;
  border-radius: 999px;
  background: rgba(28, 28, 30, 0.08);
  color: #1c1c1e;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
`;

const UserNameText = styled.span`
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  color: #475569;
`;

const MobileOnly = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
  }
`;

const DesktopOnly = styled.div`
  @media (max-width: 768px) {
    display: none !important;
  }
`;

const SearchWrapper = styled.div`
  position: relative;
  margin-bottom: 16px;
`;

const SearchIcon = styled(FiSearch)`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #8e8e93;
`;

const SearchInput = styled.input`
  width: 100%;
  height: 52px;
  padding: 0 18px 0 46px;
  border: 1px solid #e8e8ed;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.9);
  color: #1d1d1f;
  font-size: 15px;
  outline: none;
  transition: 0.2s ease;

  &::placeholder {
    color: #8e8e93;
  }

  &:focus {
    border-color: #d8d8de;
    background: #ffffff;
    box-shadow: 0 0 0 4px rgba(0, 122, 255, 0.07);
  }
`;

/** “Nova inscrição”: gradiente alinhado ao login (azul/roxo), sem fundo escuro sólido. */
const PrimaryButton = styled.button`
  position: relative;
  overflow: hidden;
  height: 42px;
  padding: 0 16px;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, #5a8dee 0%, #6c63ff 100%);
  color: #f8fafc;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  box-shadow:
    0 10px 22px rgba(90, 141, 238, 0.22),
    0 4px 10px rgba(108, 99, 255, 0.12),
    0 1px 0 rgba(255, 255, 255, 0.2) inset;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    filter 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    inset: 1px;
    border-radius: 11px;
    pointer-events: none;
    border: 1px solid rgba(255, 255, 255, 0.1);
  }

  & svg {
    flex-shrink: 0;
  }

  &:hover {
    filter: brightness(1.05);
    transform: translateY(-1px);
    box-shadow:
      0 14px 28px rgba(90, 141, 238, 0.28),
      0 6px 14px rgba(108, 99, 255, 0.16),
      0 1px 0 rgba(255, 255, 255, 0.22) inset;
  }

  &:active {
    transform: translateY(0) scale(0.99);
    filter: brightness(0.98);
    box-shadow:
      0 6px 16px rgba(90, 141, 238, 0.18),
      0 2px 6px rgba(108, 99, 255, 0.1),
      0 1px 0 rgba(255, 255, 255, 0.16) inset;
  }

  &:focus-visible {
    outline: none;
    box-shadow:
      0 10px 22px rgba(90, 141, 238, 0.22),
      0 4px 10px rgba(108, 99, 255, 0.12),
      0 0 0 3px rgba(90, 141, 238, 0.35),
      0 1px 0 rgba(255, 255, 255, 0.2) inset;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.62;
    filter: grayscale(0.15);
    transform: none;
    box-shadow: none;

    &:hover {
      filter: grayscale(0.15);
      transform: none;
      box-shadow: none;
    }

    &:active {
      transform: none;
    }
  }

  @media (max-width: 768px) {
    width: 100%;
    height: 42px;
    min-width: 0;
    padding: 0 14px;
  }
`;

const MobileHeaderButton = styled.button`
  height: 42px;
  min-height: 42px;
  min-width: 42px;
  width: auto;
  padding: 0 12px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 13px;
  background: rgba(255, 255, 255, 0.68);
  backdrop-filter: blur(14px);
  color: #2c2c2e;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.84);
    border-color: rgba(15, 23, 42, 0.12);
  }

  &:active {
    transform: scale(0.99);
  }
`;

const TopActionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 18px;
`;

const DesktopActionGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
`;

const SecondaryActionButton = styled.button`
  height: 42px;
  padding: 0 14px;
  border: 1px solid var(--btn-secondary-border);
  border-radius: 13px;
  background: rgba(255, 255, 255, 0.92);
  color: #3a3a3c;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: #ffffff;
    border-color: #d4d4dc;
  }
`;

const GhostActionButton = styled.button`
  height: 42px;
  padding: 0 10px;
  border: 1px solid transparent;
  border-radius: 12px;
  background: transparent;
  color: var(--text-secondary);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    background: #f2f2f7;
    color: var(--text-primary);
  }
`;

const StateCard = styled.div`
  min-height: 140px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(15, 23, 42, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: var(--text-secondary);
  font-size: 15px;
  text-align: center;
  padding: 24px;
  box-shadow:
    0 1px 2px rgba(15, 23, 42, 0.03),
    0 16px 28px -22px rgba(15, 23, 42, 0.12);
  animation: ${cardEnter} 0.28s ease both;
`;

const Section = styled.section`
  padding: 6px 0 0;

  & + & {
    margin-top: 16px;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #ececf1;

  @media (max-width: 768px) {
    margin-bottom: 12px;
    padding-bottom: 10px;
    align-items: center;
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 6px;
  }
`;

const SectionHeaderMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 32px;
  line-height: 1.08;
  letter-spacing: -0.04em;
  font-weight: 700;
  color: #1d1d1f;

  @media (max-width: 768px) {
    font-size: 26px;
  }
`;

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 24px;
  padding: 0 9px;
  border-radius: 999px;
  background: rgba(242, 242, 247, 0.95);
  color: #5f616a;
  font-size: 11px;
  font-weight: 600;
`;

const ActiveGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
`;

const RegistrationCard = styled.article`
  padding: 22px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(15, 23, 42, 0.06);
  box-shadow:
    0 1px 2px rgba(15, 23, 42, 0.03),
    0 18px 34px -24px rgba(15, 23, 42, 0.14);
  transition: transform 0.18s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  animation: ${cardEnter} 0.28s ease both;

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(15, 23, 42, 0.08);
    box-shadow:
      0 2px 6px rgba(15, 23, 42, 0.04),
      0 22px 38px -24px rgba(15, 23, 42, 0.16);
  }

  @media (max-width: 768px) {
    padding: 18px;
    border-radius: 18px;
  }
`;

const CardIdentityBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
  margin-bottom: 16px;
`;

const CardParticipantName = styled.h3`
  margin: 0;
  color: var(--text-primary);
  font-size: 1.125rem;
  font-weight: 600;
  line-height: 1.35;
  letter-spacing: -0.02em;
`;

const CardInstitution = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1.4;
  color: #6b7280;
`;

/** Linha de ações: secundárias à esquerda, Pagar à direita (desktop); coluna no mobile. */
const CardActionsBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px 14px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SecondaryActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  flex: 1 1 auto;
  min-width: 0;

  @media (max-width: 768px) {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

/** Pagar à direita no desktop; largura total abaixo das outras ações no mobile. */
const PayAction = styled.div`
  display: inline-flex;
  flex-shrink: 0;
  margin-left: auto;

  @media (max-width: 768px) {
    width: 100%;
    margin-left: 0;
    justify-content: stretch;
  }
`;

const PaidBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 36px;
  padding: 0 12px;
  border-radius: 999px;
  background: rgba(240, 253, 244, 0.95);
  border: 1px solid rgba(22, 163, 74, 0.22);
  color: #166534;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  white-space: nowrap;
`;

const Button = styled.button`
  min-height: ${({ $variant }) => ($variant === 'outline' ? '36px' : '40px')};
  padding: ${({ $variant }) => ($variant === 'outline' ? '0 15px' : '0 14px')};
  border-radius: ${({ $variant }) => ($variant === 'outline' ? '10px' : '10px')};
  min-width: ${({ $variant }) => ($variant === 'primary' ? '152px' : '0')};
  border: 1px solid
    ${({ $variant }) =>
      $variant === 'primary'
        ? 'transparent'
        : $variant === 'outline'
          ? 'rgba(109, 93, 246, 0.28)'
          : 'rgba(15, 23, 42, 0.08)'};
  background: ${({ $variant }) =>
    $variant === 'primary'
      ? '#1c1c1e'
      : $variant === 'outline'
        ? 'linear-gradient(165deg, rgba(255, 255, 255, 0.96) 0%, rgba(109, 93, 246, 0.1) 100%)'
        : 'rgba(248, 250, 252, 0.92)'};
  color: ${({ $variant }) =>
    $variant === 'primary' ? '#ffffff' : $variant === 'outline' ? '#4a54d0' : '#334155'};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ $variant }) => ($variant === 'outline' ? '7px' : '6px')};
  font-size: ${({ $variant }) => ($variant === 'outline' ? '13px' : '13px')};
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  box-shadow: ${({ $variant }) =>
    $variant === 'primary'
      ? '0 2px 8px rgba(0, 0, 0, 0.12)'
      : $variant === 'outline'
        ? '0 1px 3px rgba(79, 110, 247, 0.1)'
        : '0 1px 2px rgba(15, 23, 42, 0.03)'};
  transform: scale(1);
  transition:
    background 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    color 0.2s ease,
    transform 0.2s ease,
    opacity 0.2s ease;

  & svg {
    flex-shrink: 0;
  }

  &:hover {
    background: ${({ $variant }) =>
      $variant === 'primary'
        ? '#1c1c1e'
        : $variant === 'outline'
          ? 'linear-gradient(165deg, rgba(255, 255, 255, 0.98) 0%, rgba(109, 93, 246, 0.16) 100%)'
          : '#ffffff'};
    border-color: ${({ $variant }) =>
      $variant === 'primary'
        ? 'transparent'
        : $variant === 'outline'
          ? 'rgba(79, 110, 247, 0.42)'
          : 'rgba(15, 23, 42, 0.1)'};
    color: ${({ $variant }) =>
      $variant === 'primary' ? '#ffffff' : $variant === 'outline' ? '#3d47c4' : undefined};
    box-shadow: ${({ $variant }) =>
      $variant === 'primary'
        ? '0 2px 8px rgba(0, 0, 0, 0.12)'
        : $variant === 'outline'
          ? '0 2px 12px rgba(79, 110, 247, 0.2)'
          : '0 2px 4px rgba(15, 23, 42, 0.04)'};
  }

  &:active:not(:disabled) {
    transform: scale(0.97);
    opacity: 0.9;
  }

  &:disabled {
    opacity: 1;
    cursor: progress;
    transform: scale(1);
    box-shadow: ${({ $variant }) =>
      $variant === 'primary'
        ? '0 8px 18px -14px rgba(0, 0, 0, 0.28)'
        : $variant === 'outline'
          ? '0 1px 3px rgba(79, 110, 247, 0.08)'
          : '0 1px 2px rgba(15, 23, 42, 0.03)'};
  }

  @media (max-width: 768px) {
    width: 100%;
    flex: 1 1 auto;
    min-width: 0;
  }
`;

const InlineEmpty = styled.div`
  border-radius: 18px;
  border: 1px dashed #d6d6dc;
  padding: 18px;
  color: var(--text-secondary);
  background: #fafafa;
  font-size: 14px;
`;

const ArchiveShell = styled.div`
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.76);
  border: 1px solid rgba(15, 23, 42, 0.05);
  overflow: hidden;
  box-shadow:
    0 1px 2px rgba(15, 23, 42, 0.025),
    0 12px 24px -22px rgba(15, 23, 42, 0.1);
  animation: ${cardEnter} 0.28s ease both;
`;

const ArchiveToggle = styled.button`
  width: 100%;
  min-height: 56px;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 18px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(248, 250, 252, 0.78);
  }
`;

const ArchiveTitle = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 4px;
  color: #475569;
`;

const ArchiveTitleMain = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: 600;
`;

const ArchiveHint = styled.span`
  font-size: 11px;
  color: #94a3b8;
`;

const ArchiveCount = styled.span`
  padding: 0 8px;
  min-height: 22px;
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  background: rgba(241, 245, 249, 0.92);
  color: #64748b;
  font-size: 11px;
  font-weight: 600;
`;

const ArchiveChevron = styled(FiChevronDown)`
  color: #94a3b8;
  transition: transform 0.2s ease;
  transform: rotate(${({ $expanded }) => ($expanded ? '180deg' : '0deg')});
`;

const ArchivePanel = styled.div`
  overflow: hidden;
  max-height: ${({ $expanded }) => ($expanded ? '1200px' : '0')};
  opacity: ${({ $expanded }) => ($expanded ? 1 : 0)};
  transition: max-height 0.28s ease, opacity 0.18s ease;
`;

const ArchiveList = styled.div`
  padding: 0 18px 10px;
`;

const ArchiveRow = styled.div`
  min-height: 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-top: 1px solid rgba(15, 23, 42, 0.05);
`;

const ArchiveName = styled.span`
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
  padding: 12px 0;
`;

const ArchiveBadge = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 20px;
  padding: 0 8px;
  border-radius: 999px;
  background: rgba(241, 245, 249, 0.92);
  color: #94a3b8;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const ArchiveActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const ArchiveActionButton = styled.button`
  min-height: 31px;
  padding: 0 11px;
  border-radius: 10px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: rgba(255, 255, 255, 0.94);
  color: #475569;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: #ffffff;
    border-color: rgba(15, 23, 42, 0.12);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
    background: rgba(248, 250, 252, 0.9);
    color: #94a3b8;

    &:hover {
      background: rgba(248, 250, 252, 0.9);
      border-color: rgba(15, 23, 42, 0.08);
    }
  }
`;

const EventInfoShell = styled(ArchiveShell)`
  margin-bottom: 18px;
`;

const EventInfoList = styled.div`
  padding: 0 18px 14px;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    padding: 0 16px 12px;
  }
`;

const EventInfoSummary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-top: 10px;
  border-top: 1px solid rgba(15, 23, 42, 0.05);

  @media (max-width: 768px) {
    gap: 12px;
  }
`;

const EventInfoTopGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr);
  gap: 14px 18px;
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const EventInfoIdentity = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

const EventInfoEyebrow = styled.span`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: #94a3b8;
`;

const EventInfoHeadline = styled.h4`
  margin: 0;
  font-size: 20px;
  line-height: 1.15;
  letter-spacing: -0.03em;
  font-weight: 700;
  color: #1f2937;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const EventInfoSubheadline = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: #64748b;
`;

const EventInfoMetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

const EventInfoMetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const EventInfoLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #94a3b8;
`;

const EventInfoValue = styled.span`
  font-size: 14px;
  line-height: 1.5;
  font-weight: 600;
  color: #334155;
  white-space: pre-line;
`;

const EventInfoValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

const EventInfoValueCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-height: 0;
  padding: 12px 14px;
  border-radius: 14px;
  background: rgba(248, 250, 252, 0.82);
  border: 1px solid rgba(15, 23, 42, 0.05);
`;

const EventInfoValueAmount = styled.span`
  font-size: 16px;
  line-height: 1.3;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #1f2937;
`;

const EventInfoAddressLink = styled.a`
  display: inline-flex;
  align-items: flex-start;
  width: fit-content;
  max-width: 100%;
  color: #334155;
  text-decoration: none;
  transition: color 0.2s ease, opacity 0.2s ease, transform 0.2s ease;

  &:hover {
    color: #111827;
    opacity: 0.86;
    transform: translateY(-1px);
  }
`;

const ConfirmOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(29, 29, 31, 0.22);
  display: ${({ $open }) => ($open ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 70;
  padding: 16px;
`;

const ConfirmModal = styled.div`
  width: min(520px, calc(100vw - 24px));
  border-radius: 18px;
  border: 1px solid #ececf1;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 14px 30px rgba(0, 0, 0, 0.08);
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ConfirmTitle = styled.h3`
  margin: 0;
  font-size: 19px;
  font-weight: 700;
  color: #1d1d1f;
`;

const ConfirmText = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.55;
  color: #5f616a;
`;

const ConfirmActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 4px;

  @media (max-width: 768px) {
    flex-direction: column-reverse;
    align-items: stretch;
  }
`;

const DrawerOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.34);
  backdrop-filter: blur(2px);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: opacity 0.22s ease;
  z-index: 40;

  @media (min-width: 769px) {
    display: none;
  }
`;

const DrawerPanel = styled.aside`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(82vw, 340px);
  height: 100dvh;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(18px);
  border-left: 1px solid rgba(236, 236, 241, 0.92);
  border-top-left-radius: 20px;
  border-bottom-left-radius: 20px;
  padding: max(16px, env(safe-area-inset-top, 0px)) 14px
    max(16px, env(safe-area-inset-bottom, 0px));
  transform: translateX(${({ $open }) => ($open ? '0' : '100%')});
  transition: transform 0.24s ease;
  box-shadow:
    -8px 0 24px rgba(15, 23, 42, 0.08),
    -24px 0 48px rgba(15, 23, 42, 0.12);
  z-index: 50;
  display: flex;
  flex-direction: column;
  gap: 14px;

  @media (min-width: 769px) {
    display: none;
  }
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f1f1f4;
`;

const DrawerTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #1d1d1f;
`;

const DrawerCloseButton = styled.button`
  width: 34px;
  height: 34px;
  border: 1px solid #ececf1;
  border-radius: 12px;
  background: #fafafc;
  color: #636366;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const DrawerNav = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DrawerNavButton = styled.button`
  min-height: 44px;
  padding: 0 14px;
  border: 1px solid transparent;
  border-radius: 14px;
  background: #f8f8fa;
  color: #1d1d1f;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: #ffffff;
    border-color: #ececf1;
  }
`;

const DrawerFooter = styled.div`
  margin-top: auto;
  padding-top: 14px;
  border-top: 1px solid #f1f1f4;
`;

const DrawerDivider = styled.div`
  height: 1px;
  background: #f1f1f4;
  margin: 10px 0 8px;
`;

const DrawerLogoutButton = styled.button`
  min-height: 44px;
  width: 100%;
  padding: 0 14px;
  border-radius: 14px;
  border: 1px solid transparent;
  background: transparent;
  color: #6e6e73;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: #f8f8fa;
    border-color: #ececf1;
    color: #1d1d1f;
  }
`;

const DrawerInstagramLink = styled.a`
  min-height: 44px;
  padding: 0 14px;
  border-radius: 14px;
  background: transparent;
  color: #6e6e73;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    background: #f8f8fa;
    color: #1d1d1f;
  }
`;

const Spinner = styled(FiLoader)`
  flex-shrink: 0;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const getFirstName = (name = '') =>
  String(name)
    .trim()
    .split(/\s+/)
    .filter(Boolean)[0] || 'participante';

/** Rótulo da IE para o card (IE guarda o nome da lista; "outro" usa otherInstitution). */
const getInstitutionDisplayName = (item = {}) => {
  const ie = String(item.IE ?? '').trim();
  const other = String(item.otherInstitution ?? '').trim();
  if (!ie && !other) return '';
  const ieLower = ie.toLowerCase();
  if (ieLower === 'outro') return other || '';
  return ie;
};

const isEventConfigured = (evento) =>
  Boolean(
    evento &&
      [
        evento.nome,
        evento.nomeExibicao,
        evento.ano,
        evento.dataInicio,
        evento.dataFim,
        evento.localNome,
        evento.localEndereco,
        evento.valorTrabalhador,
        evento.valorConfraternista,
        evento.valorPequenoCompanheiro,
      ].some((value) => String(value || '').trim() !== '')
  );

const formatEventDate = (value) => {
  if (!value) return 'Nao informado';

  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;

  return new Intl.DateTimeFormat('pt-BR').format(parsed);
};

const formatEventMoney = (value) => {
  if (value === '' || value == null) return 'Nao informado';

  const normalized = Number(String(value).replace(',', '.'));
  if (!Number.isFinite(normalized)) return String(value);

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(normalized);
};

const formatEventPeriod = (start, end) => {
  if (!start && !end) return 'Nao informado';
  if (start && end) return `${formatEventDate(start)} ate ${formatEventDate(end)}`;
  return formatEventDate(start || end);
};

const isIosDevice = () => {
  if (typeof navigator === 'undefined') return false;

  const userAgent = navigator.userAgent || '';
  return /iPad|iPhone|iPod/.test(userAgent);
};

const buildMapSearchUrl = (address) => {
  const normalizedAddress = String(address || '').trim();
  if (!normalizedAddress) return '';

  const query = encodeURIComponent(normalizedAddress);
  return isIosDevice()
    ? `http://maps.apple.com/?q=${query}`
    : `https://www.google.com/maps/search/?api=1&query=${query}`;
};

const buildReenrollmentPayload = (item = {}) => {
  const pickValue = (...keys) => {
    for (const key of keys) {
      const value = item?.[key];
      if (value !== undefined && value !== null && value !== '') {
        return value;
      }
    }
    return '';
  };

  const toDateOrNull = (value) => {
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  return {
    nomeCompleto: pickValue('nomeCompleto', 'nome_completo'),
    dataNascimento: toDateOrNull(pickValue('dataNascimento', 'data_nascimento', 'nascimento')),
    nomeCracha: pickValue('nomeCracha', 'nomeCrachá', 'nome_no_cracha', 'nomeNoCracha'),
    sexo: item.sexo || '',
    email: item.email || '',
    telefone: item.telefone || '',
    tipoParticipacao: item.tipoParticipacao || '',
    nomeCompletoResponsavel: item.nomeCompletoResponsavel || '',
    documentoResponsavel: item.documentoResponsavel || '',
    telefoneResponsavel: item.telefoneResponsavel || '',
    cep: item.cep || '',
    estado: item.estado || '',
    cidade: item.cidade || '',
    bairro: item.bairro || '',
    logradouro: item.logradouro || '',
    numero: item.numero || '',
    complemento: item.complemento || '',
    medicacao: item.medicacao || '',
    alergia: item.alergia || '',
    outrasInformacoes: item.outrasInformacoes || '',
    IE: item.IE || '',
    vegetariano: item.vegetariano || '',
    nomeSocial: item.nomeSocial || '',
    outroGenero: item.outroGenero || '',
    otherInstitution: item.otherInstitution || '',
    deficienciaAuditiva: Boolean(item.deficienciaAuditiva),
    deficienciaAutismo: Boolean(item.deficienciaAutismo),
    deficienciaIntelectual: Boolean(item.deficienciaIntelectual),
    deficienciaParalisiaCerebral: Boolean(item.deficienciaParalisiaCerebral),
    deficienciaVisual: Boolean(item.deficienciaVisual),
    deficienciaFisica: Boolean(item.deficienciaFisica),
    deficienciaOutra: Boolean(item.deficienciaOutra),
    deficienciaOutraDescricao: item.deficienciaOutraDescricao || '',
    // intentionally reset for new cycle / business validation
    primeiraComejaca: false,
    comissao: '',
  };
};

const Dashboard = () => {
  const [search, setSearch] = useState('');
  const [inscricoes, setInscricoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [eventoAtual, setEventoAtual] = useState(null);
  const [eventoLoaded, setEventoLoaded] = useState(false);
  const [loadingItemId, setLoadingItemId] = useState(null);
  const [isArchivedExpanded, setIsArchivedExpanded] = useState(false);
  const [isEventInfoExpanded, setIsEventInfoExpanded] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [pendingReenrollmentItem, setPendingReenrollmentItem] = useState(null);
  const [isReenrollmentLoading, setIsReenrollmentLoading] = useState(false);
  const [pagamentoResumo, setPagamentoResumo] = useState(null);
  const [mpReturnHintOpen, setMpReturnHintOpen] = useState(false);

  const navigate = useNavigate();
  const API_URL = getApiBaseUrl();
  const {
    loading: registrationLoading,
    closed: registrationsClosed,
    closedButtonLabel,
  } = useRegistrationStatus();
  const isAdmin = useMemo(() => {
    try {
      const storedUser = JSON.parse(localStorage.getItem('user') || 'null');
      const role = storedUser?.role || localStorage.getItem('role');
      return role === 'admin' || role === 'admin_total';
    } catch {
      const role = localStorage.getItem('role');
      return role === 'admin' || role === 'admin_total';
    }
  }, []);
  const isAdminTotal = useMemo(() => {
    try {
      const storedUser = JSON.parse(localStorage.getItem('user') || 'null');
      const role = storedUser?.role || localStorage.getItem('role');
      return role === 'admin_total';
    } catch {
      return localStorage.getItem('role') === 'admin_total';
    }
  }, []);
  const currentUser = useMemo(() => {
    try {
      const storedUser = JSON.parse(localStorage.getItem('user') || 'null');
      const fullName = String(storedUser?.fullName || storedUser?.name || '').trim();
      const initials = fullName
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0))
        .join('')
        .toUpperCase();

      return {
        fullName,
        initials: initials || 'U',
      };
    } catch {
      return {
        fullName: '',
        initials: 'U',
      };
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('isVerified');
    navigate('/');
  };

  const handlePagamento = async (itemId) => {
    setLoadingItemId(itemId);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/auth/pagamento/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const rawPoint = response.data?.init_point ?? response.data?.initPoint;
      const linkPagamento = String(rawPoint ?? '').trim();
      const resumo = response.data?.resumo;

      if (
        linkPagamento &&
        isHttpOrHttpsUrl(linkPagamento) &&
        resumo &&
        typeof resumo === 'object' &&
        !Array.isArray(resumo)
      ) {
        setPagamentoResumo({ initPoint: linkPagamento, resumo });
      } else {
        toast.error(PAYMENT_LINK_ERROR);
      }
    } catch (err) {
      const data = err?.response?.data;
      const msg =
        (typeof data?.error === 'string' && data.error) ||
        (typeof data?.message === 'string' && data.message) ||
        (typeof err?.message === 'string' && err.message) ||
        PAYMENT_LINK_ERROR;

      if (err?.response?.status === 400 && data?.alreadyPaid) {
        toast.info('Esta inscrição já está paga.');
      } else {
        toast.error(msg);
      }
    } finally {
      setLoadingItemId(null);
    }
  };

  useEffect(() => {
    const fetchInscricoes = async () => {
      try {
        setLoading(true);

        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/api/auth/obterinscricoes`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = Array.isArray(response.data?.data) ? response.data.data : [];
        setInscricoes(data);
        setError(null);
      } catch (requestError) {
        setError(requestError.response?.data?.error || 'Erro ao carregar inscrições');

        if (requestError.response?.status === 401) {
          handleLogout();
        }
      } finally {
        setLoading(false);
      }
    };

    fetchInscricoes();
  }, [API_URL, navigate]);

  useEffect(() => {
    if (tryConsumeMercadoPagoCheckoutPending()) {
      setMpReturnHintOpen(true);
    }
  }, []);

  useEffect(() => {
    const fetchEventoAtual = async () => {
      if (!isAdminTotal) {
        setEventoLoaded(true);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/api/evento`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = response.data?.data;
        setEventoAtual(isEventConfigured(data) ? data : null);
      } catch (requestError) {
        if (![401, 403].includes(requestError?.response?.status)) {
          console.error('[dashboard] erro ao buscar evento atual:', requestError);
        }
        setEventoAtual(null);
      } finally {
        setEventoLoaded(true);
      }
    };

    fetchEventoAtual();
  }, [API_URL, isAdminTotal]);

  const eventInfoData = useMemo(() => {
    if (!eventoAtual) return null;

    const endereco = eventoAtual.localEndereco || '';
    const nomePrincipal = eventoAtual.nomeExibicao || eventoAtual.nome || 'Nao informado';
    const nomeSecundario = eventoAtual.nomeCompleto || eventoAtual.nome || 'Nao informado';

    return {
      nome: eventoAtual.nome || 'Nao informado',
      nomeExibicao: eventoAtual.nomeExibicao || 'Nao informado',
      nomeCompleto: eventoAtual.nomeCompleto || 'Nao informado',
      nomePrincipal,
      nomeSecundario,
      periodo: formatEventPeriod(eventoAtual.dataInicio, eventoAtual.dataFim),
      local: eventoAtual.localNome || 'Nao informado',
      endereco: endereco || 'Nao informado',
      enderecoUrl: endereco ? buildMapSearchUrl(endereco) : '',
      valorTrabalhador: formatEventMoney(eventoAtual.valorTrabalhador),
      valorConfraternista: formatEventMoney(eventoAtual.valorConfraternista),
      valorPequenoCompanheiro: formatEventMoney(eventoAtual.valorPequenoCompanheiro),
    };
  }, [eventoAtual]);

  const filteredData = useMemo(() => {
    if (!Array.isArray(inscricoes)) return [];

    const term = search.toLowerCase().trim();
    if (!term) return inscricoes;

    return inscricoes.filter((item) => {
      return (
        item.nomeCompleto?.toLowerCase().includes(term) ||
        item.IE?.toLowerCase().includes(term) ||
        item.email?.toLowerCase().includes(term)
      );
    });
  }, [inscricoes, search]);

  const groupedSections = useMemo(() => {
    return filteredData.reduce(
      (groups, item) => {
        const lifecycle = getInscricaoLifecycle(item);
        groups[lifecycle.sectionKey].push({ ...item, lifecycle });
        return groups;
      },
      { active: [], archived: [] }
    );
  }, [filteredData]);

  const handleNavigateToStats = () => {
    setIsMobileMenuOpen(false);
    navigate('/pagamentos');
  };

  const handleNavigateToInstitution = () => {
    setIsMobileMenuOpen(false);
    navigate('/instituicao');
  };

  const handleNavigateToEventConfig = () => {
    setIsMobileMenuOpen(false);
    navigate('/admin/evento');
  };

  const openReenrollmentConfirm = (item) => {
    setPendingReenrollmentItem(item);
  };

  const closeReenrollmentConfirm = () => {
    setPendingReenrollmentItem(null);
  };

  const confirmReenrollment = async () => {
    if (!pendingReenrollmentItem) return;

    try {
      setIsReenrollmentLoading(true);
      const token = localStorage.getItem('token');

      const response = await axios.get(
        `${API_URL}/api/auth/print/${pendingReenrollmentItem.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const sourceData = response?.data?.data || pendingReenrollmentItem;
      const firstName = getFirstName(sourceData.nomeCompleto || pendingReenrollmentItem.nomeCompleto);
      const prefillData = buildReenrollmentPayload(sourceData);

      setPendingReenrollmentItem(null);
      navigate('/inscrever', {
        state: {
          reenrollment: {
            firstName,
            prefillData,
          },
        },
      });
    } catch {
      alert('Não foi possível carregar os dados para reinscrição agora.');
    } finally {
      setIsReenrollmentLoading(false);
    }
  };

  return (
    <Container>
      <ConfirmOverlay $open={Boolean(pendingReenrollmentItem)} onClick={closeReenrollmentConfirm}>
        <ConfirmModal onClick={(event) => event.stopPropagation()}>
          <ConfirmTitle>Inscrever {ACTIVE_REGISTRATION_YEAR}?</ConfirmTitle>
          <ConfirmText>
            Deseja usar os dados de {getFirstName(pendingReenrollmentItem?.nomeCompleto)} para
            iniciar uma nova inscrição em {ACTIVE_REGISTRATION_YEAR}? Você poderá revisar e ajustar as informações
            antes de confirmar.
          </ConfirmText>
          <ConfirmActions>
            <ArchiveActionButton
              type="button"
              onClick={closeReenrollmentConfirm}
              disabled={isReenrollmentLoading}
            >
              Cancelar
            </ArchiveActionButton>
            <Button
              type="button"
              $variant="primary"
              onClick={confirmReenrollment}
              disabled={isReenrollmentLoading}
            >
              {isReenrollmentLoading ? 'Carregando...' : 'Continuar'}
            </Button>
          </ConfirmActions>
        </ConfirmModal>
      </ConfirmOverlay>

      <AppHeader
        glass
        titleContent={
          <HeaderIdentity>
            <HeaderBrand>{EVENT.name}</HeaderBrand>
            <HeaderMetaRow>
              <HeaderPageTitleRow>
                <HeaderPageTitle>Inscrições {ACTIVE_REGISTRATION_YEAR}</HeaderPageTitle>
                <HeaderPageMeta>Total: {groupedSections.active.length}</HeaderPageMeta>
              </HeaderPageTitleRow>
              {currentUser.fullName ? (
                <UserBadge aria-label={`Usuário logado: ${currentUser.fullName}`}>
                  <UserAvatar>{currentUser.initials}</UserAvatar>
                  <UserNameText>{currentUser.fullName}</UserNameText>
                </UserBadge>
              ) : null}
            </HeaderMetaRow>
          </HeaderIdentity>
        }
        rightContent={
          <MobileOnly>
            <MobileHeaderButton
              type="button"
              onClick={() => setIsMobileMenuOpen(true)}
              aria-label="Abrir menu"
            >
              <FiMenu size={18} />
            </MobileHeaderButton>
          </MobileOnly>
        }
      />

      <DrawerOverlay $open={isMobileMenuOpen} onClick={() => setIsMobileMenuOpen(false)} />
      <DrawerPanel $open={isMobileMenuOpen}>
        <DrawerHeader>
          <DrawerTitle>Menu</DrawerTitle>
          <DrawerCloseButton type="button" onClick={() => setIsMobileMenuOpen(false)}>
            <FiX size={18} />
          </DrawerCloseButton>
        </DrawerHeader>

        <DrawerNav>
          {isAdmin && (
            <>
              <DrawerNavButton type="button" onClick={handleNavigateToStats}>
                <FiBarChart2 size={16} />
                Estatísticas
              </DrawerNavButton>
              <DrawerNavButton type="button" onClick={handleNavigateToInstitution}>
                <FiPlus size={16} />
                Adicionar IE
              </DrawerNavButton>
            </>
          )}
          {isAdminTotal && (
            <DrawerNavButton type="button" onClick={handleNavigateToEventConfig}>
              <FiSettings size={16} />
              Configurar Evento
            </DrawerNavButton>
          )}
        </DrawerNav>

        <DrawerFooter>
          <DrawerInstagramLink
            href={process.env.REACT_APP_INSTAGRAM_URL || 'https://www.instagram.com/comejaca'}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <FaInstagram size={16} />
            Instagram
          </DrawerInstagramLink>

          <DrawerDivider />

          <DrawerLogoutButton type="button" onClick={handleLogout}>
            <FiLogOut size={16} />
            Sair
          </DrawerLogoutButton>
        </DrawerFooter>
      </DrawerPanel>

      <Content>
        {eventoLoaded && isAdminTotal ? (
          <EventInfoShell>
            <ArchiveToggle
              type="button"
              onClick={() => setIsEventInfoExpanded((prev) => !prev)}
              aria-expanded={isEventInfoExpanded}
              aria-controls="dashboard-event-info"
            >
              <ArchiveTitle>
                <ArchiveTitleMain>
                  <span>Informações do encontro</span>
                </ArchiveTitleMain>
                <ArchiveHint>
                  {isEventInfoExpanded ? 'Toque para recolher' : 'Toque para expandir'}
                </ArchiveHint>
              </ArchiveTitle>

              <ArchiveChevron $expanded={isEventInfoExpanded} />
            </ArchiveToggle>

            <ArchivePanel id="dashboard-event-info" $expanded={isEventInfoExpanded}>
              {eventInfoData ? (
                <EventInfoList>
                  <EventInfoSummary>
                    <EventInfoTopGrid>
                      <EventInfoIdentity>
                        <EventInfoEyebrow>Encontro</EventInfoEyebrow>
                        <EventInfoHeadline>{eventInfoData.nomePrincipal}</EventInfoHeadline>
                        {eventInfoData.nomeSecundario !== eventInfoData.nomePrincipal ? (
                          <EventInfoSubheadline>{eventInfoData.nomeSecundario}</EventInfoSubheadline>
                        ) : null}
                      </EventInfoIdentity>

                      <EventInfoMetaGrid>
                        <EventInfoMetaItem>
                          <EventInfoLabel>Periodo</EventInfoLabel>
                          <EventInfoValue>{eventInfoData.periodo}</EventInfoValue>
                        </EventInfoMetaItem>

                        <EventInfoMetaItem>
                          <EventInfoLabel>Local</EventInfoLabel>
                          <EventInfoValue>{eventInfoData.local}</EventInfoValue>
                        </EventInfoMetaItem>

                        <EventInfoMetaItem style={{ gridColumn: '1 / -1' }}>
                          <EventInfoLabel>Endereco</EventInfoLabel>
                          {eventInfoData.enderecoUrl ? (
                            <EventInfoAddressLink
                              href={eventInfoData.enderecoUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <EventInfoValue>{eventInfoData.endereco}</EventInfoValue>
                            </EventInfoAddressLink>
                          ) : (
                            <EventInfoValue>{eventInfoData.endereco}</EventInfoValue>
                          )}
                        </EventInfoMetaItem>
                      </EventInfoMetaGrid>
                    </EventInfoTopGrid>

                    <EventInfoValuesGrid>
                      <EventInfoValueCard>
                        <EventInfoLabel>Trabalhador</EventInfoLabel>
                        <EventInfoValueAmount>{eventInfoData.valorTrabalhador}</EventInfoValueAmount>
                      </EventInfoValueCard>

                      <EventInfoValueCard>
                        <EventInfoLabel>Confraternista</EventInfoLabel>
                        <EventInfoValueAmount>{eventInfoData.valorConfraternista}</EventInfoValueAmount>
                      </EventInfoValueCard>

                      <EventInfoValueCard>
                        <EventInfoLabel>Pequeno companheiro</EventInfoLabel>
                        <EventInfoValueAmount>
                          {eventInfoData.valorPequenoCompanheiro}
                        </EventInfoValueAmount>
                      </EventInfoValueCard>
                    </EventInfoValuesGrid>
                  </EventInfoSummary>
                </EventInfoList>
              ) : (
                <ArchiveList>
                  <InlineEmpty>Nenhuma informação do encontro cadastrada ainda.</InlineEmpty>
                </ArchiveList>
              )}
            </ArchivePanel>
          </EventInfoShell>
        ) : null}

        <TopActionBar>
          <MobileOnly>
            <PrimaryButton
              type="button"
              disabled={registrationLoading || registrationsClosed}
              onClick={() => {
                if (!registrationLoading && !registrationsClosed) {
                  navigate('/inscrever');
                }
              }}
            >
              <FiPlus size={16} />
              {registrationsClosed ? closedButtonLabel : 'Nova inscrição'}
            </PrimaryButton>
          </MobileOnly>

          <DesktopOnly>
            <DesktopActionGroup>
              <PrimaryButton
                type="button"
                disabled={registrationLoading || registrationsClosed}
                onClick={() => {
                  if (!registrationLoading && !registrationsClosed) {
                    navigate('/inscrever');
                  }
                }}
              >
                <FiPlus size={16} />
                {registrationsClosed ? closedButtonLabel : 'Nova inscrição'}
              </PrimaryButton>

              {isAdmin && (
                <>
                  <SecondaryActionButton type="button" onClick={handleNavigateToStats}>
                    <FiBarChart2 size={16} />
                    Estatísticas
                  </SecondaryActionButton>
                  <SecondaryActionButton type="button" onClick={handleNavigateToInstitution}>
                    <FiPlus size={16} />
                    Adicionar IE
                  </SecondaryActionButton>
                </>
              )}
              {isAdminTotal && (
                <SecondaryActionButton type="button" onClick={handleNavigateToEventConfig}>
                  <FiSettings size={16} />
                  Configurar Evento
                </SecondaryActionButton>
              )}

              <GhostActionButton type="button" onClick={handleLogout}>
                <FiLogOut size={16} />
                Sair
              </GhostActionButton>
            </DesktopActionGroup>
          </DesktopOnly>
        </TopActionBar>

        {loading ? (
          <StateCard>
            <Spinner size={18} />
            Carregando inscrições...
          </StateCard>
        ) : error ? (
          <StateCard>{error}</StateCard>
        ) : inscricoes.length === 0 ? (
          <StateCard>Estamos aguardando sua primeira inscrição</StateCard>
        ) : (
          <>
            <Section>
     {/*          <SearchWrapper>
                <SearchIcon size={18} />
                <SearchInput
                  type="text"
                  placeholder="Pesquisar por nome, IE ou e-mail"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </SearchWrapper> */}

              {filteredData.length === 0 ? (
                <StateCard>
                  <FiSearch size={18} />
                  Nenhum resultado encontrado
                </StateCard>
              ) : groupedSections.active.length > 0 ? (
                <ActiveGrid>
                  {groupedSections.active.map((item) => {
                    const pago = isPagamentoPago(item.statusPagamento);
                    const institutionLabel = getInstitutionDisplayName(item);
                    const showPay =
                      item.lifecycle.actions.canPay && !pago;

                    return (
                      <RegistrationCard key={item.id}>
                        <CardIdentityBlock>
                          <CardParticipantName>{item.nomeCompleto}</CardParticipantName>
                          {institutionLabel ? (
                            <CardInstitution>{institutionLabel}</CardInstitution>
                          ) : null}
                        </CardIdentityBlock>

                        <CardActionsBar>
                          <SecondaryActions>
                            <Button
                              type="button"
                              onClick={() => navigate(`/imprimir/${item.id}`)}
                            >
                              <FiPrinter size={14} />
                              Imprimir
                            </Button>

                            <Button
                              type="button"
                              onClick={() => navigate(`/atualizar/${item.id}`)}
                            >
                              <FiEdit size={14} />
                              Editar
                            </Button>
                          </SecondaryActions>

                          {showPay ? (
                            <PayAction>
                              <Button
                                type="button"
                                $variant="outline"
                                onClick={() => handlePagamento(item.id)}
                                disabled={loadingItemId === item.id}
                              >
                                {loadingItemId === item.id ? (
                                  <>
                                    <Spinner size={14} />
                                    Processando
                                  </>
                                ) : (
                                  <>
                                    <FiCreditCard size={15} strokeWidth={2.25} aria-hidden />
                                    Pagar
                                  </>
                                )}
                              </Button>
                            </PayAction>
                          ) : pago ? (
                            <PayAction>
                              <PaidBadge role="status" aria-label="Pagamento confirmado">
                                Pago
                              </PaidBadge>
                            </PayAction>
                          ) : null}
                        </CardActionsBar>
                      </RegistrationCard>
                    );
                  })}
                </ActiveGrid>
              ) : (
                <InlineEmpty>
                  Nenhuma inscrição de {ACTIVE_REGISTRATION_YEAR} encontrada no momento.
                </InlineEmpty>
              )}
            </Section>

            {groupedSections.archived.length > 0 && (
              <Section>
                <ArchiveShell>
                  <ArchiveToggle
                    type="button"
                    onClick={() => setIsArchivedExpanded((prev) => !prev)}
                    aria-expanded={isArchivedExpanded}
                    aria-controls="archived-registrations"
                  >
                    <ArchiveTitle>
                      <ArchiveTitleMain>
                        <span>Inscrições 2025</span>
                        <ArchiveCount>{groupedSections.archived.length}</ArchiveCount>
                      </ArchiveTitleMain>
                      <ArchiveHint>{isArchivedExpanded ? 'Toque para recolher' : 'Toque para expandir'}</ArchiveHint>
                    </ArchiveTitle>

                    <ArchiveChevron $expanded={isArchivedExpanded} />
                  </ArchiveToggle>

                  <ArchivePanel
                    id="archived-registrations"
                    $expanded={isArchivedExpanded}
                  >
                    <ArchiveList>
                      {groupedSections.archived.map((item) => (
                        <ArchiveRow key={item.id}>
                          <ArchiveName>{item.nomeCompleto}</ArchiveName>

                          <ArchiveActions>
                            {item.lifecycle.cycleYear === 2025 && (
                              <ArchiveActionButton
                                type="button"
                                disabled={registrationLoading || registrationsClosed}
                                onClick={() => {
                                  if (!registrationLoading && !registrationsClosed) {
                                    openReenrollmentConfirm(item);
                                  }
                                }}
                              >
                                {registrationsClosed
                                  ? closedButtonLabel
                                  : `Inscrever ${ACTIVE_REGISTRATION_YEAR}`}
                              </ArchiveActionButton>
                            )}

                            {item.lifecycle.badgeLabel && (
                              <ArchiveBadge>{item.lifecycle.badgeLabel}</ArchiveBadge>
                            )}
                          </ArchiveActions>
                        </ArchiveRow>
                      ))}
                    </ArchiveList>
                  </ArchivePanel>
                </ArchiveShell>
              </Section>
            )}
          </>
        )}

      </Content>

      <PagamentoResumoModal
        isOpen={Boolean(pagamentoResumo)}
        resumo={pagamentoResumo?.resumo ?? null}
        initPoint={pagamentoResumo?.initPoint ?? ''}
        onCancel={() => setPagamentoResumo(null)}
        onContinue={(url) => {
          const checkoutUrl = String(url ?? '').trim();
          if (!isHttpOrHttpsUrl(checkoutUrl)) {
            toast.error(PAYMENT_LINK_ERROR);
            return;
          }
          markMercadoPagoCheckoutPending();
          window.location.assign(checkoutUrl);
        }}
      />

      <MercadoPagoReturnHintModal
        isOpen={mpReturnHintOpen}
        onClose={() => setMpReturnHintOpen(false)}
      />
    </Container>
  );
};

export default Dashboard;